/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { memo } from 'react'
import { jsx, css } from '@emotion/react'

import { CuiThemedIcon } from '@modules/cui/ThemedIcon'

import elasticLogo from '../../../files/elastic-logo-H-color.svg'
import elasticLogoDark from '../../../files/elastic-logo-H-color-dark.svg'

import { LoginButton, LogoutButton, SignupButton } from './HeaderButton'

import type { ReactElement } from 'react'
import type { LandingPageContext } from '../types'

export interface Props {
  locationQueryString: string
  pageContext?: LandingPageContext
  onPageContextSwitch?: (pageContext: LandingPageContext) => void
}

const styles = {
  logo: css({
    position: 'relative',
    height: '30px', // from landingPageContainer.scss
  }),
  elasticLogo: css({
    width: 'auto',
    height: '58px',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  }),
}

const getSwitchedContext = (pageContext?: LandingPageContext): LandingPageContext => {
  const pageContextName = pageContext?.name

  if (pageContextName === 'signup') {
    return { name: 'login' }
  }

  return { name: 'signup' }
}

const Header = ({ locationQueryString, pageContext, onPageContextSwitch }: Props): ReactElement => {
  const renderButton = (): ReactElement | null => {
    if (!pageContext) {
      return null
    }

    const onClick = onPageContextSwitch
      ? () => onPageContextSwitch(getSwitchedContext(pageContext))
      : undefined
    const { name: pageContextName, contextSwitchLocationState } = pageContext
    const buttonProps = {
      locationQueryString,
      onClick,
      locationState: contextSwitchLocationState,
    }

    if (pageContextName === 'signup') {
      return <LoginButton {...buttonProps} />
    }

    if (pageContextName === 'loggedIn') {
      return <LogoutButton {...buttonProps} />
    }

    return <SignupButton {...buttonProps} />
  }

  return (
    <div css={styles.logo}>
      {renderButton()}

      <CuiThemedIcon
        size='xxl'
        lightType={elasticLogo}
        darkType={elasticLogoDark}
        css={styles.elasticLogo}
      />
    </div>
  )
}

export default memo(Header)
